import { Layout } from "@/components/Layout";
import { motion } from "framer-motion";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Scale, ShieldCheck, MessageSquare, Repeat2, Sparkles } from "lucide-react";

/**
 * Single legal hub — consolidates the previous Terms + Privacy + Complaints +
 * Subscription pages into one document with anchored sections, reducing the
 * site's total page count and making compliance content easier to maintain.
 */
const SECTIONS = [
  { id: "terms",         label: "Terms of Service", icon: Scale },
  { id: "privacy",       label: "Privacy Policy",   icon: ShieldCheck },
  { id: "complaints",    label: "Complaints",       icon: MessageSquare },
  { id: "subscription",  label: "Subscriptions",    icon: Repeat2 },
  { id: "ai-disclaimer", label: "AI Disclaimer",    icon: Sparkles },
];

const LegalPage = () => {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace("#", ""));
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [hash]);

  return (
    <Layout>
      <section className="py-20 md:py-28 bg-[#F9FAF9]">
        <div className="container-clean">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <span className="inline-flex items-center gap-2 bg-[#E8F5E9] text-[#2D5016] px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Scale className="w-4 h-4" />
              Legal & Policies
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-[#1A2E12] tracking-tight mb-4">
              The fine print.
            </h1>
            <p className="text-lg text-[#5C6B57]">
              UK law-compliant terms, privacy, complaints and subscription policies — written straight, no jargon. Last updated February 2026.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Anchored nav */}
      <nav className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-[#E2E8E0]">
        <div className="container-clean py-3 overflow-x-auto">
          <ul className="flex gap-2 md:gap-3 min-w-max">
            {SECTIONS.map((s) => (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#E2E8E0] text-sm text-[#5C6B57] hover:border-[#2D5016] hover:text-[#2D5016] transition"
                  data-testid={`legal-nav-${s.id}`}
                >
                  <s.icon className="w-4 h-4" />
                  {s.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      <div className="container-clean py-16 max-w-3xl space-y-20 text-[#1A2E12] leading-relaxed">
        {/* ============================== TERMS ============================== */}
        <section id="terms" data-testid="legal-section-terms" className="scroll-mt-24">
          <h2 className="text-3xl font-semibold mb-6 flex items-center gap-2"><Scale className="w-6 h-6 text-[#2D5016]" /> Terms of Service</h2>
          <p className="text-sm text-[#5C6B57] mb-4">These terms govern your use of cleankinservices.co.uk and any cleaning service you book through us. By booking, you accept these terms.</p>
          <div className="space-y-4 text-[#1A2E12]">
            <div>
              <h3 className="font-semibold mb-1">1. Who we are</h3>
              <p>Clean Kin is a London &amp; Birmingham cleaning service. Director: Sherron Beckles. All written communication: <strong>info@cleankinservices.co.uk</strong>.</p>
            </div>
            <div>
              <h3 className="font-semibold mb-1">2. Service standards</h3>
              <p>We dispatch professional, vetted cleaners trained to deliver a defined standard. We do not guarantee the same cleaner on every visit — we do guarantee the standard.</p>
            </div>
            <div>
              <h3 className="font-semibold mb-1">3. Bookings &amp; payment</h3>
              <p>Bookings are confirmed once payment is received. Every paid booking produces an invoice and a receipt, both available in your customer dashboard.</p>
            </div>
            <div>
              <h3 className="font-semibold mb-1">4. Cancellations</h3>
              <p>Cancellations more than 24 hours before a scheduled visit are free of charge. Cancellations within 24 hours are charged at 50%. No-shows are charged in full.</p>
            </div>
            <div>
              <h3 className="font-semibold mb-1">5. Liability</h3>
              <p>We carry public liability insurance. Damage claims must be reported within 24 hours of the visit by email. We are not liable for pre-existing damage or items of exceptional value not disclosed at booking.</p>
            </div>
            <div>
              <h3 className="font-semibold mb-1">6. Governing law</h3>
              <p>These terms are governed by the laws of England and Wales. Consumer rights under the Consumer Rights Act 2015 are not affected.</p>
            </div>
          </div>
        </section>

        {/* ============================== PRIVACY ============================ */}
        <section id="privacy" data-testid="legal-section-privacy" className="scroll-mt-24">
          <h2 className="text-3xl font-semibold mb-6 flex items-center gap-2"><ShieldCheck className="w-6 h-6 text-[#2D5016]" /> Privacy Policy</h2>
          <p className="text-sm text-[#5C6B57] mb-4">We process personal data lawfully under the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.</p>
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold mb-1">Data controller</h3>
              <p>Clean Kin (info@cleankinservices.co.uk) is the data controller for personal data you provide.</p>
            </div>
            <div>
              <h3 className="font-semibold mb-1">What we collect</h3>
              <ul className="list-disc list-inside space-y-1">
                <li>Identity &amp; contact: name, email, phone, billing &amp; service address.</li>
                <li>Booking data: service, date, time window, property details, special instructions, payment status.</li>
                <li>Technical data: device, browser, IP address &amp; cookie identifiers (functional &amp; analytics).</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-1">Lawful basis</h3>
              <p>Contractual necessity (to deliver bookings), legitimate interest (operations, fraud prevention, marketing of similar services), and consent (where you opt-in to newsletters or analytics).</p>
            </div>
            <div>
              <h3 className="font-semibold mb-1">How long we keep it</h3>
              <p>Booking and invoice records: 6 years (UK HMRC retention). Marketing contacts: until you unsubscribe. Server logs: 90 days.</p>
            </div>
            <div>
              <h3 className="font-semibold mb-1">Sharing</h3>
              <p>Stripe (payments), Resend (transactional email when enabled), and our hosting provider. We never sell personal data. We do not transfer data outside the UK/EEA without appropriate safeguards.</p>
            </div>
            <div>
              <h3 className="font-semibold mb-1">Your rights</h3>
              <p>You may access, correct, port, restrict or erase your data, and object to direct marketing. Email <strong>info@cleankinservices.co.uk</strong> with the subject <em>Data request</em>. You may also complain to the Information Commissioner&apos;s Office (ICO) at <a className="text-[#2D5016] underline" href="https://ico.org.uk/make-a-complaint/" target="_blank" rel="noreferrer">ico.org.uk/make-a-complaint</a>.</p>
            </div>
            <div>
              <h3 className="font-semibold mb-1">Cookies</h3>
              <p>We use a small number of essential cookies (session, auth) and optional analytics cookies. You can manage cookie preferences from your browser at any time.</p>
            </div>
          </div>
        </section>

        {/* ============================== COMPLAINTS ========================= */}
        <section id="complaints" data-testid="legal-section-complaints" className="scroll-mt-24">
          <h2 className="text-3xl font-semibold mb-6 flex items-center gap-2"><MessageSquare className="w-6 h-6 text-[#2D5016]" /> Complaints Handling Policy</h2>
          <p className="text-sm text-[#5C6B57] mb-4">We take complaints seriously and follow the UK Consumer Rights Act 2015 timeline for resolution.</p>
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold mb-1">Stage 1 — Raise it</h3>
              <p>Email <strong>info@cleankinservices.co.uk</strong> with the subject <em>Complaint</em>. Include your booking reference, date of service, and what went wrong. We acknowledge within 2 business days.</p>
            </div>
            <div>
              <h3 className="font-semibold mb-1">Stage 2 — Investigation</h3>
              <p>A senior team member reviews the booking, cleaner notes, photographic evidence (if any), and your statement. We respond with a proposed resolution within 14 calendar days.</p>
            </div>
            <div>
              <h3 className="font-semibold mb-1">Stage 3 — Final response</h3>
              <p>If you are not satisfied with the Stage 2 outcome, you may request escalation. A final written response is issued within 8 weeks of the original complaint (UK consumer law standard).</p>
            </div>
            <div>
              <h3 className="font-semibold mb-1">Stage 4 — Independent redress</h3>
              <p>If the matter remains unresolved after 8 weeks, you are entitled to escalate via:</p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li><a className="text-[#2D5016] underline" href="https://www.citizensadvice.org.uk/consumer/" target="_blank" rel="noreferrer">Citizens Advice consumer service</a></li>
                <li>An approved Alternative Dispute Resolution (ADR) scheme</li>
                <li>The Small Claims Court (claims under £10,000 in England &amp; Wales)</li>
              </ul>
            </div>
          </div>
        </section>

        {/* ============================== SUBSCRIPTION ======================= */}
        <section id="subscription" data-testid="legal-section-subscription" className="scroll-mt-24">
          <h2 className="text-3xl font-semibold mb-6 flex items-center gap-2"><Repeat2 className="w-6 h-6 text-[#2D5016]" /> Subscription Policy</h2>
          <p className="text-sm text-[#5C6B57] mb-4">Subscriptions cover our Standard and Deep Clean services on a Weekly, Bi-Weekly or Monthly schedule. End-of-Tenancy is one-off only.</p>
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold mb-1">How it works</h3>
              <p>You pay per visit at your chosen frequency&apos;s rate. Recurring payments are taken at the start of each billing cycle and a receipt is issued automatically.</p>
            </div>
            <div>
              <h3 className="font-semibold mb-1">Pausing</h3>
              <p>Pause your subscription at any time from your dashboard. Paused subscriptions retain your locked-in pricing for 90 days; after that, you resume at the current published rate.</p>
            </div>
            <div>
              <h3 className="font-semibold mb-1">Termination — 30-day notice</h3>
              <p><strong>Either party may terminate a subscription with 30 calendar days&apos; written notice</strong> (email is sufficient). During the notice period, scheduled visits and the cancellation policy in the Terms continue to apply. After termination, no further charges are taken.</p>
            </div>
            <div>
              <h3 className="font-semibold mb-1">14-day cooling-off (new subscribers)</h3>
              <p>Under the Consumer Contracts Regulations 2013, you may cancel a new subscription within 14 days for a full refund, less the pro-rated cost of any visit already performed at your express request.</p>
            </div>
            <div>
              <h3 className="font-semibold mb-1">Price changes</h3>
              <p>We give at least 30 days&apos; written notice of any subscription price change. If you do not wish to continue at the new rate, you may terminate without further notice.</p>
            </div>
          </div>
        </section>

        {/* ============================== AI DISCLAIMER ====================== */}
        <section id="ai-disclaimer" data-testid="legal-section-ai-disclaimer" className="scroll-mt-24">
          <h2 className="text-3xl font-semibold mb-6 flex items-center gap-2"><Sparkles className="w-6 h-6 text-[#2D5016]" /> AI Content &amp; Imagery Disclaimer</h2>
          <p className="text-[#1A2E12]">Some written content, illustrative imagery and on-site visuals on cleankinservices.co.uk are generated or assisted by artificial intelligence tools. AI-generated content is reviewed by a human before publication, but it is provided for illustrative purposes and may not depict actual Clean Kin cleaners, properties, or specific service outcomes. Service descriptions, pricing, and policy text are authoritative; any AI-generated narrative is supplementary.</p>
          <p className="mt-3 text-[#1A2E12]">If you spot something that looks inaccurate, please email <strong>info@cleankinservices.co.uk</strong> and we&apos;ll review and correct it promptly.</p>
        </section>
      </div>
    </Layout>
  );
};

export default LegalPage;
